/**
 * Opens a source document in place, from any moment or passage that cites it, with the preview of
 * the Documents tab — or on its own page, for the browser's "open in a new tab".
 */

import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router";
import dayjs from "dayjs";
import { ArrowLeft, Calendar, FileText, Loader2 } from "lucide-react";
import { Trans } from "@lingui/macro";
import API from "@app/services/api";
import { Badge } from "@app/components/ui/badge";
import { Button } from "@app/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import ViewOnlyDocument from "./ViewOnlyDocument";
import {
  docTypeColor,
  isPlainLeftClick,
  SourceDocumentContext,
  sourceDocumentPath,
} from "@app/utils/source-document";

/** One entry of `GET /patient/{tenant}/{id}/documents`. */
export interface PatientDocument {
  id: string;
  id_document: string;
  id_evenement: string;
  document_name: string;
  file_name: string;
  date: string;
  doc_type: string;
  body: string | null;
  body_html: string | null;
  has_file: boolean;
  file_mime_type: string | null;
  document_metadata?: Record<string, unknown> | null;
  event_debut?: string | null;
  event_categorie?: string | null;
}

/**
 * Document metadata entries to show a practitioner. Scalar values are kept
 * unchanged (empty ones render as "—"). Only nested objects are dropped —
 * these are internal provenance (e.g. the `split` object written for
 * multi-patient PDF children), which is not human-readable and cannot be
 * rendered as a React child.
 */
function displayableMetadata(
  metadata: Record<string, unknown>,
): Array<[string, string]> {
  return Object.entries(metadata).flatMap(([key, value]) => {
    if (value !== null && typeof value === "object") return [];
    const text =
      value === null || value === undefined || value === ""
        ? "—"
        : String(value);
    return [[key, text]];
  });
}

function ModalFrame({
  title,
  onClose,
  children,
}: {
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <Card className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden">
        <CardHeader className="flex-shrink-0 border-b border-slate-200">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">{title}</div>
            <Button variant="outline" size="sm" onClick={onClose}>
              <Trans>Fermer</Trans>
            </Button>
          </div>
        </CardHeader>
        {children}
      </Card>
    </div>
  );
}

function documentName(document: PatientDocument): string {
  return document.document_name || document.file_name || "Document";
}

/** Matches either id the reading may cite: the document's own, or its source id. */
function findDocument(
  documents: PatientDocument[],
  docId: string,
): PatientDocument | null {
  return (
    documents.find((doc) => doc.id === docId || doc.id_document === docId) ??
    null
  );
}

/** The dossier's documents, read live from the source once `enabled` turns true. */
function usePatientDocuments(
  tenantId: string,
  patientId: string,
  enabled: boolean,
): { documents: PatientDocument[] | null; failed: boolean } {
  const [documents, setDocuments] = useState<PatientDocument[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setDocuments(null);
    setFailed(false);
  }, [tenantId, patientId]);

  useEffect(() => {
    if (!enabled || documents !== null || failed) return;
    let cancelled = false;
    API.get({ path: `/patient/${tenantId}/${patientId}/documents` })
      .then((res: { data?: { documents?: PatientDocument[] } }) => {
        if (cancelled) return;
        const list = res?.data?.documents;
        setDocuments(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, documents, failed, tenantId, patientId]);

  return { documents, failed };
}

/** A link to a source document: a plain click previews it in place, while middle click, Ctrl/⌘-click
 *  and "Ouvrir dans un nouvel onglet" keep the browser's own behaviour on the document's page. */
export function SourceDocumentLink({
  onOpen,
  onClick,
  ...props
}: ComponentProps<typeof Link> & { onOpen: () => void }) {
  return (
    <Link
      {...props}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || !isPlainLeftClick(event)) return;
        event.preventDefault();
        onOpen();
      }}
    />
  );
}

/** A document's name, file, type and dates. */
function DocumentHeading({ document }: { document: PatientDocument }) {
  return (
    <>
      <CardTitle className="flex items-center gap-2 text-primary">
        <FileText className="h-5 w-5 shrink-0" />
        <span className="truncate">{documentName(document)}</span>
      </CardTitle>
      {document.file_name && (
        <p className="mt-1 text-sm text-slate-600">{document.file_name}</p>
      )}
      <div className="mt-3 flex items-center gap-3">
        {document.doc_type && (
          <Badge
            className={docTypeColor(document.doc_type)}
            variant="secondary"
          >
            {document.doc_type}
          </Badge>
        )}
        {document.date && (
          <span className="flex items-center gap-1 text-xs text-slate-500">
            <Calendar className="h-3 w-3" />
            {dayjs(document.date).format("DD/MM/YYYY")}
          </span>
        )}
        {document.event_categorie && (
          <span className="text-xs text-slate-500">
            {document.event_categorie}
            {document.event_debut &&
              ` — ${dayjs(document.event_debut).format("DD/MM/YYYY")}`}
          </span>
        )}
      </div>
    </>
  );
}

/** A document's metadata, then its file, its HTML or its text. */
function DocumentBody({
  tenantId,
  patientId,
  document,
}: {
  tenantId: string;
  patientId: string;
  document: PatientDocument;
}) {
  // Blob URL for inline PDF rendering — revoked when the document changes or the modal closes.
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if (!document.has_file) return;
    let cancelled = false;
    let blobUrl: string | null = null;
    setPdfLoading(true);
    fetch(
      API.getUrl(
        `/patient/${tenantId}/${patientId}/documents/${document.id}/file`,
      ),
      { credentials: "include" },
    )
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(blobUrl);
      })
      .catch(() => {
        if (!cancelled) setPdfBlobUrl(null);
      })
      .finally(() => {
        if (!cancelled) setPdfLoading(false);
      });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setPdfBlobUrl(null);
    };
  }, [tenantId, patientId, document.id, document.has_file]);

  // A Word 97-2003 file's text, which the server reads (antiword); null when it has none to give.
  const loadLegacyWordText = useCallback(async (): Promise<string | null> => {
    const res = await fetch(
      API.getUrl(
        `/patient/${tenantId}/${patientId}/documents/${document.id}/file`,
        { text: true },
      ),
      { credentials: "include" },
    );
    if (res.status === 415) return null;
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
  }, [tenantId, patientId, document.id]);

  const name = documentName(document);
  const metadataEntries = displayableMetadata(document.document_metadata ?? {});

  return (
    <>
      <CardContent className="flex-1 overflow-y-auto p-6">
        {metadataEntries.length > 0 && (
          <div className="mb-6">
            <h4 className="mb-3 text-sm font-medium text-slate-700">
              <Trans>Métadonnées du document</Trans>
            </h4>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {metadataEntries.map(([key, value]) => (
                <div
                  key={key}
                  className="rounded bg-slate-50 px-3 py-2 text-sm"
                >
                  <span className="font-medium text-slate-600">{key}</span>
                  <span className="mx-1 text-slate-400">:</span>
                  <span className="text-slate-900">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Binary file (PDF etc.) — inline viewer, no download affordance */}
        {document.has_file ? (
          pdfLoading ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-slate-500">
                <Trans>Chargement du fichier...</Trans>
              </p>
            </div>
          ) : pdfBlobUrl ? (
            <ViewOnlyDocument
              blobUrl={pdfBlobUrl}
              mimeType={document.file_mime_type}
              title={name}
              loadLegacyWordText={loadLegacyWordText}
            />
          ) : (
            <div className="py-8 text-center">
              <FileText className="mx-auto mb-2 h-10 w-10 text-slate-300" />
              <p className="text-sm text-slate-500">
                <Trans>Impossible de charger le fichier</Trans>
              </p>
            </div>
          )
        ) : document.body_html ? (
          /* HTML content from the source rich-text editor */
          <iframe
            srcDoc={document.body_html}
            sandbox="allow-same-origin"
            className="w-full rounded border border-slate-200"
            style={{ height: "60vh" }}
            title={name}
          />
        ) : document.body ? (
          <div className="prose prose-sm max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700">
              {document.body}
            </pre>
          </div>
        ) : (
          <div className="py-8 text-center">
            <FileText className="mx-auto mb-2 h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">
              <Trans>Pas de contenu disponible pour ce document</Trans>
            </p>
          </div>
        )}
      </CardContent>
      <div className="flex-shrink-0 border-t border-slate-200 bg-slate-50 p-4">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>
            <Trans>Document : {document.id_document}</Trans>
          </span>
          {document.id_evenement && (
            <span>
              <Trans>Évènement : {document.id_evenement}</Trans>
            </span>
          )}
        </div>
      </div>
    </>
  );
}

/** The preview of one document, over the page. */
export function DocumentPreviewModal({
  tenantId,
  patientId,
  document,
  onClose,
}: {
  tenantId: string;
  patientId: string;
  document: PatientDocument;
  onClose: () => void;
}) {
  return (
    <ModalFrame
      onClose={onClose}
      title={<DocumentHeading document={document} />}
    >
      <DocumentBody
        tenantId={tenantId}
        patientId={patientId}
        document={document}
      />
    </ModalFrame>
  );
}

function DocumentStatus({ loading }: { loading: boolean }) {
  return loading ? (
    <div className="flex flex-col items-center gap-3 py-12">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm text-slate-500">
        <Trans>Chargement du document...</Trans>
      </p>
    </div>
  ) : (
    // The id comes from the dossier's reading: a miss is the source, not the id.
    <p className="py-8 text-center text-sm text-slate-500">
      <Trans>
        Ce document n'est pas disponible dans la source pour le moment.
      </Trans>
    </p>
  );
}

/** One document on its own page — where "Ouvrir dans un nouvel onglet" lands. */
export function SourceDocumentPage({
  tenantId,
  patientId,
  docId,
}: {
  tenantId: string;
  patientId: string;
  docId: string;
}) {
  const { documents, failed } = usePatientDocuments(tenantId, patientId, true);
  const document = documents ? findDocument(documents, docId) : null;

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-6">
      <Link
        to={`/patient/${tenantId}/${patientId}`}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        <Trans>Retour au dossier</Trans>
      </Link>
      <Card className="overflow-hidden">
        {document ? (
          <>
            <CardHeader className="border-b border-slate-200">
              <DocumentHeading document={document} />
            </CardHeader>
            <DocumentBody
              tenantId={tenantId}
              patientId={patientId}
              document={document}
            />
          </>
        ) : (
          <CardContent className="p-6">
            <DocumentStatus loading={documents === null && !failed} />
          </CardContent>
        )}
      </Card>
    </div>
  );
}

export function SourceDocumentProvider({
  tenantId,
  patientId,
  children,
}: {
  tenantId: string;
  patientId: string;
  children: React.ReactNode;
}) {
  const [docId, setDocId] = useState<string | null>(null);
  // Fetched on first open: the list is a live read of the source system.
  const { documents, failed } = usePatientDocuments(
    tenantId,
    patientId,
    docId !== null,
  );

  useEffect(() => {
    setDocId(null);
  }, [tenantId, patientId]);

  const open = useCallback((id: string) => setDocId(id), []);
  const close = useCallback(() => setDocId(null), []);
  const access = useMemo(
    () => ({
      open,
      href: (id: string) => sourceDocumentPath(tenantId, patientId, id),
    }),
    [open, tenantId, patientId],
  );
  const document =
    docId !== null && documents !== null
      ? findDocument(documents, docId)
      : null;

  return (
    <SourceDocumentContext.Provider value={access}>
      {children}
      {docId !== null &&
        (document ? (
          <DocumentPreviewModal
            tenantId={tenantId}
            patientId={patientId}
            document={document}
            onClose={close}
          />
        ) : (
          <ModalFrame
            onClose={close}
            title={
              <CardTitle className="flex items-center gap-2 text-primary">
                <FileText className="h-5 w-5 shrink-0" />
                <Trans>Document source</Trans>
              </CardTitle>
            }
          >
            <CardContent className="p-6">
              <DocumentStatus loading={documents === null && !failed} />
            </CardContent>
          </ModalFrame>
        ))}
    </SourceDocumentContext.Provider>
  );
}
